import React, { useCallback, useState, useEffect } from 'react';
import {
  Type,
  Backdrop,
  ButtonPrimary,
  ButtonTertiary,
  DialogConfirmation,
  IconX,
  ButtonIcon,
  IconSpotifyLogo,
  Image,
} from '@spotify-internal/encore-creator-web';
import {
  black,
  spotifyGreenHighlight,
  white,
  spacer8,
  spacer12,
} from '@spotify-internal/encore-foundation';
import styled from 'styled-components';
import moment from 'moment';
import html2canvas from 'html2canvas';
import {
  AddCopy,
  AddsList,
  AddRow,
  AddRowCoverArt,
  AddsCardContainer,
  CoverArt,
  Header,
  HeaderContainer,
  Heading,
} from './styles';
import { ChartAdd } from '../../../lib/Api/homeCards';
import { FormattedMessage } from 'react-intl';
import { HomeTracker, CardType } from '../../../lib/Tracking/HomeTracker';

type Props = {
  imageUrl: string;
  chartAdds: ChartAdd[];
  networkId: string | null;
  tracker: HomeTracker;
};

export function ChartAddsCard({ imageUrl, chartAdds, networkId, tracker }: Props) {
  const featuredChartAdd = chartAdds[0];
  const [shareCardCanvas, setShareCardCanvas] = useState<HTMLCanvasElement>();
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [shareCardChartAdd, setShareCardChartAdd] = useState<ChartAdd>();

  useEffect(() => {
    tracker.trackImpression(
      tracker.makeHomeCardImpressionEvent(chartAdds[0].homeCardId, CardType.ChartAdd, networkId),
    );
  }, []);

  const logInteraction = ({
    podcastId,
    networkId,
    homeCardId,
    destinationUrl,
  }: {
    podcastId: string;
    networkId: string | null;
    homeCardId: string;
    destinationUrl: string;
  }) => {
    tracker.trackInteraction(
      tracker.makeHomeCardInteractionEvent(
        homeCardId,
        CardType.ChartAdd,
        networkId,
        podcastId,
        destinationUrl,
      ),
    );
  };

  const shareCardRef = useCallback(async el => {
    if (el !== null) {
      // explicitly set the 'fill' attribute because html2canvas isn't properly reading from the 'fill' CSS property
      el.querySelector('svg path').setAttribute('fill', white);
      const canvas = await html2canvas(el, {
        allowTaint: true,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        scale: 3,
      });
      canvas.style.width = '100%';
      canvas.style.maxWidth = '400px';
      canvas.style.height = 'auto';
      canvas.style.display = 'block';
      el.replaceWith(canvas);
      setShareCardCanvas(canvas);
    }
  }, []);

  const downloadImage = async () => {
    if (shareCardCanvas && shareCardChartAdd) {
      const { homeCardId, podcastId } = shareCardChartAdd;
      tracker.trackChartShareCardDownload(
        tracker.makeHomeCardInteractionEvent(
          homeCardId,
          CardType.ChartAdd,
          networkId,
          podcastId,
          'download_to_png',
        ),
      );
      shareCardCanvas.toBlob(blob => {
        let a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${shareCardChartAdd.chartLink}.png`;
        a.click();
        setTimeout(() => {
          URL.revokeObjectURL(a.href);
        }, 40000);
      });
    }
  };

  const handleShowDownloadModal = (chartAdd: ChartAdd) => {
    logInteraction({
      podcastId: chartAdd.podcastId,
      networkId,
      homeCardId: chartAdd.homeCardId,
      destinationUrl: 'show_chart_share_card_modal',
    });
    setShareCardChartAdd(chartAdd);
    // hide the scrollbar in order to prevent html2canvas from including it as padding in the generated canvas
    document.body.style.overflow = 'hidden';
    setShowDownloadModal(true);
  };

  const handleHideDownloadModal = () => {
    document.body.style.overflow = 'visible';
    setShowDownloadModal(false);
  };

  const renderDownloadModal = () => {
    if (!shareCardChartAdd) return null;
    return (
      <Backdrop center>
        <DialogConfirmation
          dialogTitle={
            <DialogTitle>
              <Type as="h1" variant={Type.heading3}>
                <FormattedMessage
                  id="home.chard-add-download-title"
                  defaultMessage="Share your achievement!"
                />
              </Type>
              <ButtonIcon onClick={handleHideDownloadModal}>
                <IconX aria-label="Close" />
              </ButtonIcon>
            </DialogTitle>
          }
          body={
            <ShareCardWrapper>
              <ShareCard ref={shareCardRef}>
                <ChartPosition bgColor={shareCardChartAdd.chartColor}>
                  <div style={{ margin: spacer12 }}>
                    <IconSpotifyLogo iconSize={16} color={white} />
                    <ChartRank>{`#${shareCardChartAdd.chartRank}`}</ChartRank>
                    <ChartName>{shareCardChartAdd.chartName}</ChartName>
                  </div>
                  <ChartAddFooter>
                    <svg
                      width="31"
                      height="33"
                      viewBox="0 0 31 33"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 0H23C27.4183 0 31 3.58172 31 8V33H0V0Z"
                        fill="white"
                        fillOpacity="0.7"
                      />
                      <path
                        d="M21 11.0001H22.625V9.37506H21V11.0001ZM9.14905 25.1491L22.149 12.1491L19.8509 9.85101L6.85095 22.851L9.14905 25.1491ZM19.375 11.0001V22.6171H22.625V11.0001H19.375ZM21 9.37506H9.93617V12.6251H21V9.37506Z"
                        fill={shareCardChartAdd.chartColor}
                        fillOpacity="0.8"
                      />
                    </svg>
                    <div style={{ marginLeft: spacer8 }}>
                      <DateAdded>{moment(new Date()).format('MMMM D, YYYY')}</DateAdded>
                      <DetailText>{shareCardChartAdd.chartCountry}</DetailText>
                    </div>
                  </ChartAddFooter>
                </ChartPosition>
                <div style={{ backgroundColor: shareCardChartAdd.chartColor }}>
                  <Image
                    src={shareCardChartAdd.podcastCoverArtUrl}
                    alt="Podcast Artwork"
                    imageHeight="140px"
                    imageWidth="140px"
                    style={{ margin: spacer12, borderRadius: '8px' }}
                  />
                  <PodcastName>{shareCardChartAdd.podcastName}</PodcastName>
                </div>
              </ShareCard>
            </ShareCardWrapper>
          }
          footer={
            <div>
              <ButtonPrimary buttonSize={ButtonTertiary.sm} onClick={downloadImage}>
                <FormattedMessage id="home.chard-add-download-button" defaultMessage="Download" />
              </ButtonPrimary>
            </div>
          }
          style={{ width: 'auto' }}
        />
      </Backdrop>
    );
  };

  return (
    <AddsCardContainer>
      {showDownloadModal && renderDownloadModal()}
      <HeaderContainer>
        <Header bgColor={featuredChartAdd.chartColor}>
          <ChartCoverArtWrapper onClick={() => handleShowDownloadModal(featuredChartAdd)}>
            <CoverArt src={imageUrl} alt="Chart cover art" />
            <WhiteOverlay />
          </ChartCoverArtWrapper>
        </Header>
      </HeaderContainer>
      <Heading>
        <Type as="h2" typeColor={black} variant={Type.heading3}>
          <FormattedMessage
            id="home.chard-add-ranked-info"
            defaultMessage="{podcastName} is #{chartRank} on the {chartLink} chart!"
            values={{
              podcastName: featuredChartAdd.podcastName,
              chartRank: featuredChartAdd.chartRank,
              chartLink: featuredChartAdd.chartLink,
            }}
          />
        </Type>
      </Heading>
      <AddsList>
        {chartAdds.map(chartAdd => {
          const showUrl = `/podcast/${chartAdd.podcastId}`;
          return (
            <AddRow key={`${chartAdd.homeCardId}-Row`}>
              <a
                href={showUrl}
                onClick={() =>
                  logInteraction({
                    podcastId: chartAdd.podcastId,
                    networkId,
                    homeCardId: chartAdd.homeCardId,
                    destinationUrl: showUrl,
                  })
                }
              >
                <AddRowCoverArt src={chartAdd.podcastCoverArtUrl} alt={chartAdd.podcastName} />
              </a>
              <AddCopy>
                <Type as="p" variant={Type.body2}>
                  <FormattedMessage
                    id="home.chard-add-ranked-info-2"
                    defaultMessage="<boldLink>{podcastName}</boldLink> is ranked
                    <chartLinkWrapper>#{chartRank} in the {chartLink}</chartLinkWrapper> chart."
                    values={{
                      podcastName: chartAdd.podcastName,
                      boldLink: (...chunks) => (
                        <a
                          href={showUrl}
                          style={{ textDecoration: 'none' }}
                          onClick={() =>
                            logInteraction({
                              podcastId: chartAdd.podcastId,
                              networkId,
                              homeCardId: chartAdd.homeCardId,
                              destinationUrl: showUrl,
                            })
                          }
                        >
                          <Type as="span" weight={Type.bold}>
                            {chunks}
                          </Type>
                        </a>
                      ),
                      chartRank: chartAdd.chartRank,
                      chartLink: chartAdd.chartLink,
                      chartLinkWrapper: (...chunks) => (
                        <ChartPositionCopy onClick={() => handleShowDownloadModal(chartAdd)}>
                          {chunks}
                        </ChartPositionCopy>
                      ),
                    }}
                  />
                </Type>
              </AddCopy>
            </AddRow>
          );
        })}
      </AddsList>
    </AddsCardContainer>
  );
}

const DialogTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const ShareCardWrapper = styled.div`
  margin: 0 12px 20px;
`;

const ShareCard = styled.div`
  display: flex;
  width: 400px;
  height: 225px;
`;

const ChartPosition = styled.div<{ bgColor: string }>`
  background-color: ${({ bgColor }) => bgColor};
  opacity: 0.8;
  flex: 0 0 236px;
  position: relative;
`;

const ChartRank = styled.p`
  color: ${white};
  font-size: 36px;
  line-height: 40px;
  padding: 0;
  letter-spacing: -0.5px;
  font-weight: bold;
`;

const ChartName = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 24px;
  line-height: 28px;
  padding: 0;
  letter-spacing: -0.5px;
  font-weight: bold;
`;

const ChartAddFooter = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
`;

const DetailText = styled.p`
  color: ${white};
  font-size: 11px;
  font-weight: bold;
  line-height: 13px;
  padding: 0;
`;

const DateAdded = styled(DetailText)`
  color: rgba(255, 255, 255, 0.7);
`;

const PodcastName = styled(DetailText)`
  margin: 0 ${spacer12} 22px;
`;

const ChartCoverArtWrapper = styled.div`
  display: inline-block;
  position: relative;
  cursor: pointer;
`;

const WhiteOverlay = styled.div`
  background: rgba(255, 255, 255, 0.2);
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 8px;
`;

const ChartPositionCopy = styled(Type).attrs({
  forwardedAs: 'span',
  weight: Type.bold,
})`
  cursor: pointer;
  &:hover {
    color: ${spotifyGreenHighlight};
  }
`;
